import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom'
import { Users, LayoutDashboard, Store, LogOut, Receipt, ShoppingBag, RefreshCw, ExternalLink, Package } from 'lucide-react'
import { HealthCheck } from './components/HealthCheck'
import { LoginForm, RegisterForm, ProtectedRoute } from './components/auth'
import { useUserStore } from './stores/userStore'
import { SharedCartPage } from './pages/SharedCartPage'
import { API_BASE_URL, authHeaders } from './services/config'

function Brand() {
  return (
    <Link to="/dashboard" className="flex items-baseline gap-1 font-display text-xl font-bold">
      Hey Buddy<span className="text-coral">.</span>
    </Link>
  )
}

function AuthPage() {
  const [isLogin, setIsLogin] = useState(true)

  return (
    <ProtectedRoute requireAuth={false} redirectTo="/dashboard">
      <div className="flex min-h-screen">
        {/* Brand panel — the den, waiting for the party */}
        <div className="hidden w-1/2 flex-col justify-between bg-ink p-10 text-white lg:flex">
          <span className="font-display text-xl font-bold">
            Hey Buddy<span className="text-coral">.</span>
          </span>
          <div>
            <h1 className="mb-3 max-w-md font-display text-4xl font-bold leading-tight">
              Shopping is better with your people in the room.
            </h1>
            <p className="max-w-sm text-white/60">
              Start a session, share the party code, talk it over on voice, and split the bill at
              checkout.
            </p>
          </div>
          <p className="font-mono text-xs uppercase tracking-widest text-white/30">
            Shop together · Decide together · Pay together
          </p>
        </div>

        <div className="flex flex-1 items-center justify-center bg-paper p-6">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-card">
            {isLogin ? (
              <LoginForm onSuccess={() => {}} onSwitchToRegister={() => setIsLogin(false)} />
            ) : (
              <RegisterForm onSuccess={() => {}} onSwitchToLogin={() => setIsLogin(true)} />
            )}
          </div>
        </div>
      </div>
    </ProtectedRoute>
  )
}

function Navigation() {
  const { user, clearAuth } = useUserStore()
  const location = useLocation()

  const linkClass = (path: string) =>
    `flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
      location.pathname.startsWith(path)
        ? 'bg-coral-soft text-coral-deep'
        : 'text-ink/60 hover:bg-paper hover:text-ink'
    }`

  return (
    <nav className="border-b border-line bg-white">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-8">
          <Brand />
          <div className="flex items-center gap-1">
            <Link to="/dashboard" className={linkClass('/dashboard')}>
              <LayoutDashboard className="h-4 w-4" /> Backoffice
            </Link>
            <Link to="/carts" className={linkClass('/carts')}>
              <Users className="h-4 w-4" /> Party
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-sm text-ink/60 sm:block">{user?.displayName}</span>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-coral font-display text-xs font-bold text-white">
            {(user?.displayName || '?').slice(0, 1).toUpperCase()}
          </div>
          <button
            onClick={() => clearAuth()}
            className="rounded-lg p-2 text-ink/50 hover:bg-paper hover:text-ink"
            title="Log out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </nav>
  )
}

interface SalesOrder {
  id: string
  orderNumber: string
  sessionName: string
  subtotalCents: number
  currency: string
  participantCount: number
  placedAt: string
  lineItems?: { id: string; title: string; quantity: number }[]
}

interface VendorSales {
  orderCount: number
  grossCents: number
  currency: string
  averageOrderCents: number
  averagePartySize: number
  shopperCount: number
  recentOrders: SalesOrder[]
}

interface MerchantStore {
  vendor: { id: string; name: string; domain: string; status: string; lastSyncAt: string | null }
  productCount: number
  themeEditorUrl: string
}

function cents(value: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format(value / 100)
}

function DashboardPage() {
  // A merchant opening the app from their Shopify admin arrives signed in
  // through the URL, the way a storefront shopper does.
  const ready = useTokenArrival()
  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper text-ink/60">
        Opening your store…
      </div>
    )
  }
  return <Backoffice />
}

function Backoffice() {
  const { user, token } = useUserStore()
  const [store, setStore] = useState<MerchantStore | null>(null)
  const [sales, setSales] = useState<VendorSales | null>(null)
  const [syncing, setSyncing] = useState(false)

  // The backoffice is the signed-in merchant's own store and nobody
  // else's; anyone who is not a merchant simply has no store to show.
  const loadStore = () =>
    fetch(`${API_BASE_URL}/merchant/store`, { headers: authHeaders() })
      .then(res => (res.ok ? res.json() : null))
      .then(body => {
        const mine: MerchantStore | null = body?.data ?? null
        setStore(mine)
        if (!mine) return
        return fetch(`${API_BASE_URL}/vendors/${mine.vendor.id}/sales`, { headers: authHeaders() })
          .then(res => (res.ok ? res.json() : null))
          .then(body => setSales(body?.data ?? null))
      })
      .catch(() => setStore(null))

  useEffect(() => {
    if (token) loadStore()
  }, [token])

  const syncCatalog = () => {
    if (!store) return
    setSyncing(true)
    fetch(`${API_BASE_URL}/vendors/${store.vendor.id}/sync/products`, {
      method: 'POST',
      headers: authHeaders(),
    })
      .then(() => loadStore())
      .finally(() => setSyncing(false))
  }

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-paper">
        <Navigation />

        <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <header className="mb-8">
            <p className="text-sm uppercase tracking-wide text-ink/40">
              {store ? store.vendor.domain : 'Backoffice'}
            </p>
            <h1 className="text-3xl font-bold">Welcome back, {store?.vendor.name || user?.displayName}</h1>
          </header>

          {store ? (
            <div className="mb-5 flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-card sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-bold">Put the party button on your store</h2>
                <p className="text-sm text-ink/60">
                  Shoppers tap “Shop this with friends” on a product page and land in a party with it
                  already in the cart. Add the block in your theme editor, then save.
                </p>
              </div>
              {/* The theme editor will not load inside the admin's frame. */}
              <a href={store.themeEditorUrl} target="_top" className="btn-primary shrink-0">
                <ExternalLink className="h-4 w-4" /> Add to theme
              </a>
            </div>
          ) : (
            <div className="mb-5 rounded-2xl bg-white p-6 shadow-card">
              <h2 className="text-lg font-bold">No store connected</h2>
              <p className="text-sm text-ink/60">
                Install Hey Buddy from your Shopify admin and open it from there — your store’s
                parties and sales will show up here.
              </p>
            </div>
          )}

          <div className="grid gap-5 md:grid-cols-4">
            <div className="rounded-2xl bg-white p-6 shadow-card">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-aisle-soft">
                <Receipt className="h-5 w-5 text-aisle" />
              </div>
              <p className="font-mono text-3xl font-semibold">
                {sales ? cents(sales.grossCents, sales.currency) : '—'}
              </p>
              <p className="text-sm text-ink/60">Party revenue</p>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-card">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-coral-soft">
                <ShoppingBag className="h-5 w-5 text-coral-deep" />
              </div>
              <p className="font-mono text-3xl font-semibold">{sales?.orderCount ?? '—'}</p>
              <p className="text-sm text-ink/60">
                {sales && sales.orderCount > 0
                  ? `${cents(sales.averageOrderCents, sales.currency)} average`
                  : 'Orders'}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-card">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-radio-soft">
                <Users className="h-5 w-5 text-radio" />
              </div>
              <p className="font-mono text-3xl font-semibold">
                {sales && sales.orderCount > 0 ? sales.averagePartySize.toFixed(1) : '—'}
              </p>
              <p className="text-sm text-ink/60">
                {sales && sales.shopperCount > 0
                  ? `Avg party · ${sales.shopperCount} ${sales.shopperCount === 1 ? 'shopper' : 'shoppers'}`
                  : 'Avg party size'}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-card">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-paper">
                  {store ? <Package className="h-5 w-5 text-ink/60" /> : <Store className="h-5 w-5 text-ink/60" />}
                </div>
                {store && (
                  <button
                    onClick={syncCatalog}
                    disabled={syncing}
                    className="rounded-lg p-2 text-ink/50 hover:bg-paper hover:text-ink disabled:opacity-50"
                    title="Sync catalog from Shopify"
                  >
                    <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                  </button>
                )}
              </div>
              <p className="font-mono text-3xl font-semibold">{store?.productCount ?? '—'}</p>
              <p className="text-sm text-ink/60">
                {store?.vendor.lastSyncAt
                  ? `Products · synced ${new Date(store.vendor.lastSyncAt).toLocaleDateString()}`
                  : 'Products in the party catalog'}
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-3">
            <div className="rounded-2xl bg-white p-6 shadow-card lg:col-span-2">
              <h2 className="mb-4 text-lg font-bold">Recent orders</h2>
              {!sales || sales.recentOrders.length === 0 ? (
                <p className="py-6 text-center text-sm text-ink/50">
                  No party orders yet. When a group checks out, the sale lands here.
                </p>
              ) : (
                <ul className="divide-y divide-line">
                  {sales.recentOrders.map(order => (
                    <li key={order.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{order.sessionName || 'Party session'}</p>
                        <p className="font-mono text-xs text-ink/50">
                          {order.orderNumber} · {order.participantCount}{' '}
                          {order.participantCount === 1 ? 'shopper' : 'shoppers'} ·{' '}
                          {new Date(order.placedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="shrink-0 font-mono font-semibold">
                        {cents(order.subtotalCents, order.currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-card">
              <h2 className="mb-2 text-lg font-bold">Platform status</h2>
              <HealthCheck />
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between rounded-2xl bg-ink p-6 text-white">
            <div>
              <h2 className="text-lg font-bold">Take it for a spin</h2>
              <p className="text-sm text-white/60">
                Start a party session and see the shopper side of Hey Buddy.
              </p>
            </div>
            <Link to="/carts" className="btn-primary">
              <Users className="h-4 w-4" /> Open a party
            </Link>
          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}

// A shopper arriving from a merchant's storefront, or a merchant opening
// the app from their Shopify admin, carries their session in the URL:
// neither signed up here, so they have nothing else to arrive with.
// Swallow it before anything renders — a link still holding the token
// would hand over the session to whoever it was pasted to.
function useTokenArrival() {
  const setAuth = useUserStore(state => state.setAuth)
  const [ready, setReady] = useState(!window.location.search.includes('token='))

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (!token) return

    let cancelled = false
    fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => (res.ok ? res.json() : null))
      .then(user => {
        if (cancelled) return
        if (user?.id) setAuth(user, token)
      })
      .catch(() => {
        // Fall through to the lobby rather than a half-signed-in state.
      })
      .finally(() => {
        if (cancelled) return
        // Keep the cart id, drop the credential.
        params.delete('token')
        const query = params.toString()
        window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''))
        setReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [setAuth])

  return ready
}

function PartyPage() {
  const ready = useTokenArrival()

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink text-white/60">
        Opening your party…
      </div>
    )
  }

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-ink">
        <Navigation />

        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <SharedCartPage />
        </main>
      </div>
    </ProtectedRoute>
  )
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<AuthPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/carts" element={<PartyPage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  )
}

export default App
